import React from "react";
import { formFieldDefs } from "./11formFields";
import "../10/10Variable.css"; // 재사용

const VIP11Variable = () => {
  const totalVars = formFieldDefs.length;
  const conditionVars = formFieldDefs.filter((f) => f.conditions).length;

  // 조건 설명 포맷터
  const formatCond = (cond) => {
    if (!cond) return "-";
    if (typeof cond === "string") {
      return cond
        .replace(/===/g, "=")
        .replace(/!==/g, "≠")
        .replace(/ includes /g, " ∋ ")
        .replace(/"/g, "");
    }
    if (Array.isArray(cond)) return cond.map(formatCond).join(" AND ");
    if (typeof cond === "object" && cond.or) {
      return cond.or.map(formatCond).join(" OR ");
    }
    try {
      return JSON.stringify(cond);
    } catch (_) {
      return String(cond);
    }
  };

  // CSV 다운로드 함수
  const downloadCSV = () => {
    // CSV 헤더
    const headers = ["Field ID", "Label", "Type", "Unit", "Options", "Conditions", "Detail"];
    
    // CSV 데이터 생성
    const rows = formFieldDefs.map((field) => {
      const options = field.options
        ? Array.isArray(field.options)
          ? field.options.map((o) => o.label).join(" // ")
          : Object.values(field.options).join(", ")
        : "";
      const condText = formatCond(field.conditions);
      const detailText = field.detail || "";

      // CSV 특수문자 처리 (쉼표, 따옴표, 줄바꿈)
      const escapeCSV = (str) => {
        if (str === "-") return "";
        if (typeof str !== "string") str = String(str);
        if (str.includes(",") || str.includes('"') || str.includes("\n")) {
          return `"${str.replace(/"/g, '""')}"`;
        }
        return str;
      };

      return [
        escapeCSV(field.id),
        escapeCSV(field.label),
        escapeCSV(field.type),
        escapeCSV(field.unit || ""),
        escapeCSV(options),
        escapeCSV(condText),
        escapeCSV(detailText),
      ].join(",");
    });

    // CSV 문자열 생성 (BOM 추가하여 한글 깨짐 방지)
    const BOM = "\uFEFF";
    const csvContent = BOM + headers.join(",") + "\n" + rows.join("\n");

    // Blob 생성 및 다운로드
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `VIP11_Variables_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="vip-variable-container">
      {/* 요약 카드 */}
      <div className="variable-summary">
        <h2 className="summary-title">변수 정보 요약</h2>
        <div className="summary-cards">
          <div className="summary-card">
            <span className="summary-value">{totalVars}</span>
            <span className="summary-label">전체 변수</span>
          </div>
          <div className="summary-card">
            <span className="summary-value">{conditionVars}</span>
            <span className="summary-label">커스텀 변수</span>
          </div>
        </div>
        {/* CSV 다운로드 버튼 */}
        <button
          onClick={downloadCSV}
          style={{
            marginTop: "16px",
            padding: "12px 24px",
            backgroundColor: "#059669",
            color: "white",
            border: "none",
            borderRadius: "8px",
            cursor: "pointer",
            fontSize: "14px",
            fontWeight: "600",
            display: "flex",
            alignItems: "center",
            gap: "8px",
            transition: "background-color 0.2s",
          }}
          onMouseOver={(e) => (e.target.style.backgroundColor = "#047857")}
          onMouseOut={(e) => (e.target.style.backgroundColor = "#059669")}
        >
          <span style={{ fontSize: "18px" }}>📥</span>
          CSV 다운로드
        </button>
      </div>

      {/* 안내 노트 */}
      <div className="variable-note">
        <p>구현된 필드의 Field ID 및 Label 정보입니다.</p>
        <p>데이터 분석을 위한 변수의 타입 설정이 매핑되어 있습니다.</p>
        <p>
          설계 정보 변경이 필요하거나, 문의가 있을 시에는{" "}
          <strong>문의 탭</strong>을 이용해주세요.
        </p>
      </div>

      {/* 지원 타입 */}
      <h3 className="type-section-title">지원되는 데이터 타입</h3>
      <p className="type-section-desc">
        현재 데이터 분석을 위해 지정할 수 있는 Type의 종류는 다음과 같습니다.
        (괄호 안은 R / Python Pandas에서 사용되는 대표적인 자료형입니다)
      </p>

      <table className="variable-table">
        <thead>
          <tr>
            <th>Type 이름</th>
            <th>R 자료형</th>
            <th>Python dtype</th>
            <th>Encoding 예시</th>
          </tr>
        </thead>
        <tbody>
          {[
            {
              type: "text",
              r: "character",
              py: "object (string)",
              enc: "'남', '여' / 'M', 'F'",
            },
            {
              type: "number",
              r: "numeric / integer",
              py: "float64 / int64",
              enc: "1, 2, 3 … (연속형)",
            },
            {
              type: "categorical",
              r: "factor",
              py: "category",
              enc: "1=남, 0=여 등",
            },
            {
              type: "ordinal",
              r: "ordered factor",
              py: "CategoricalDtype(ordered=True)",
              enc: "Likert 1~5",
            },
            {
              type: "boolean",
              r: "logical",
              py: "bool",
              enc: "TRUE / FALSE, 1 / 0",
            },
            {
              type: "date",
              r: "Date",
              py: "datetime64[ns] (date)",
              enc: "YYYY-MM-DD",
            },
            {
              type: "datetime",
              r: "POSIXct / POSIXlt",
              py: "datetime64[ns]",
              enc: "YYYY-MM-DD HH:MM",
            },
            {
              type: "time",
              r: "hms / difftime",
              py: "timedelta64[ns]",
              enc: "HH:MM:SS",
            },
          ].map((row) => (
            <tr key={row.type}>
              <td>{row.type}</td>
              <td>{row.r}</td>
              <td>{row.py}</td>
              <td>{row.enc}</td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* 전체 변수 테이블 */}
      <div className="table-wrapper" style={{ marginTop: "24px" }}>
        <table className="variable-table">
          <thead>
            <tr>
              <th>Field ID</th>
              <th>Label</th>
              <th>Type</th>

              <th>Unit</th>
              <th>Options</th>
              <th>Conditions</th>
              <th>Detail</th>
            </tr>
          </thead>
          <tbody>
            {formFieldDefs.map((field) => {
              const options = field.options
                ? Array.isArray(field.options)
                  ? field.options.map((o) => o.label).join("//")
                  : Object.values(field.options).join(",")
                : "-";
              const condText = formatCond(field.conditions);
              const detailText = field.detail || "-";
              return (
                <tr key={field.id}>
                  <td>{field.id}</td>
                  <td>{field.label}</td>
                  <td>{field.type}</td>
                  <td>{field.unit || "-"}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{options}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{condText}</td>
                  <td style={{ whiteSpace: "nowrap" }}>{detailText}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default VIP11Variable;
